import { useMemo } from 'react';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import { ModelRuns, Report } from '@elementary/common-ui/types';
import { getModelsOnly } from '@elementary/common-ui/utils/modelsData';
import useTableAssets from '@elementary/common-ui/hooks/useTableAssets';
import useTreeViewParams from '@elementary/common-ui/hooks/useTreeViewParams';
import { isInSaaSMode } from '@elementary/common-ui/utils/common';
import useModelRunsTableDataGraphql from './useModelRunsTableDataGraphql';

const useModelRunsTableDataReport = ({
  reportData,
  enabled = true,
}: {
  reportData?: Report;
  enabled?: boolean;
}) => {
  const { selectedTreeFolderNodes, selectedTreeNode } = useSelectedTreeItem();
  const { urlTreeNode, urlSavedView } = useTreeViewParams();

  const { data: tableAssetsExtraDataMap, isFetching: isTableAssetsFetching } =
    useTableAssets({ enabled });

  const { filteredModelsData, modelRunsData } = useMemo(
    () => ({
      filteredModelsData: getModelsOnly(reportData?.models),
      modelRunsData: reportData?.model_runs,
    }),
    [reportData],
  );

  const selectedTreeNodeIds = useMemo(() => {
    if (!urlTreeNode && !urlSavedView) {
      return undefined;
    }

    if (selectedTreeFolderNodes?.length) {
      return selectedTreeFolderNodes.map((node) => node.id);
    }

    if (selectedTreeNode) {
      return [selectedTreeNode.id];
    }

    return [];
  }, [selectedTreeFolderNodes, selectedTreeNode, urlSavedView, urlTreeNode]);

  const enrichedModelRunsData = useMemo(() => {
    if (!modelRunsData || isTableAssetsFetching) return [];

    return modelRunsData.map((modelRun) => {
      const database_name =
        filteredModelsData[modelRun.unique_id]?.database_name;

      if (tableAssetsExtraDataMap.size === 0) {
        const { tags, owners, materialization } =
          filteredModelsData[modelRun.unique_id] || {};

        if (tags || owners || materialization) {
          return {
            ...modelRun,
            database_name,
            asset_metadata: {
              tags,
              owners,
              materialization,
            },
          };
        }
        return { ...modelRun, database_name };
      }

      const { isCritical, owners, tags, path, subscribers, materialization } =
        tableAssetsExtraDataMap.get(modelRun.unique_id) || {};

      return {
        ...modelRun,
        database_name,
        asset_metadata: {
          is_critical: isCritical,
          owners,
          tags,
          path,
          subscribers,
          materialization,
        },
      };
    });
  }, [
    modelRunsData,
    isTableAssetsFetching,
    tableAssetsExtraDataMap,
    filteredModelsData,
  ]);

  const tableData: ModelRuns[] = useMemo(() => {
    if (!selectedTreeNodeIds) {
      return enrichedModelRunsData;
    }

    if (enrichedModelRunsData) {
      return enrichedModelRunsData.filter((run) =>
        selectedTreeNodeIds.includes(run.unique_id),
      );
    }

    return [];
  }, [enrichedModelRunsData, selectedTreeNodeIds]);

  return {
    tableData,
    isTableAssetsFetching,
    fetchNextPage: undefined,
    hasNextPage: false,
    isError: false,
    filterOptions: null,
    isFilterOptionsFetching: false,
  };
};

const useModelRunsTableData = ({
  reportData,
  search,
}: {
  reportData?: Report;
  search?: string;
}) => {
  const isSaaS = isInSaaSMode();

  const graphqlResult = useModelRunsTableDataGraphql({
    search,
    enabled: isSaaS,
  });
  const reportResult = useModelRunsTableDataReport({
    reportData,
    enabled: !isSaaS,
  });

  return isSaaS ? graphqlResult : reportResult;
};

export default useModelRunsTableData;
